import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, Compass, Sparkles, CalendarDays, Send, LogOut, ShoppingBasket, Lock, Crown, ShieldCheck, Menu, X, ChefHat, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const links = [
  { to: "/", label: "Home", icon: LayoutGrid, premium: false },
  { to: "/browse", label: "Browse", icon: Compass, premium: false },
  { to: "/create", label: "Creative Agent", icon: Sparkles, premium: true },
  { to: "/planner", label: "Planner", icon: CalendarDays, premium: true },
  { to: "/supplies", label: "Supplies", icon: ShoppingBasket, premium: true },
];

function Avatar({ user, size = "w-8 h-8" }) {
  if (user.avatar_url) {
    return <img src={user.avatar_url} alt={user.name} className={`${size} rounded-full object-cover border border-black/15`} />;
  }
  const initial = (user.name || user.email || "?").charAt(0).toUpperCase();
  return (
    <div className={`${size} rounded-full bg-nerd-light text-white flex items-center justify-center font-display font-bold text-sm`}>
      {initial}
    </div>
  );
}

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [gate, setGate] = useState(null); // { label, mode: "login" | "upgrade" }
  const [menuOpen, setMenuOpen] = useState(false);

  const isPremium = !!(user && user.is_premium);

  const handleNav = (e, link) => {
    if (!link.premium) return;
    if (!user) { e.preventDefault(); setGate({ label: link.label, mode: "login" }); }
    else if (!user.is_premium) { e.preventDefault(); setGate({ label: link.label, mode: "upgrade" }); }
  };

  const closeGate = () => setGate(null);

  const goSupplies = () => {
    if (!user) { setGate({ label: "Supplies", mode: "login" }); return; }
    if (!user.is_premium) { setGate({ label: "Supplies", mode: "upgrade" }); return; }
    navigate("/supplies");
  };

  return (
    <header className="sticky top-0 z-50 glass-nav">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between relative">
        <div className="flex items-center gap-2">
          {user ? (
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setMenuOpen((o) => !o)}
              className="md:hidden w-10 h-10 rounded-full bg-black/5 border border-black/10 flex items-center justify-center text-nerd-light hover:bg-black/10 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          ) : (
            <Link to="/" data-testid="nav-logo-mobile" className="md:hidden flex items-center">
              <img src="/logo-white.png" alt="Flavour Intelligence" className="w-8 h-8 object-contain" />
            </Link>
          )}
          <Link to="/" data-testid="nav-logo" className="hidden md:flex items-center">
            <img src="/logo-white.png" alt="Flavour Intelligence" className="w-8 h-8 object-contain" />
          </Link>
        </div>

        {user && (
          <span className="md:hidden absolute left-1/2 -translate-x-1/2 sr-only">Flavour Intelligence</span>
        )}

        <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {links.map((link) => {
            const { to, label, icon: Icon, premium } = link;
            const active = pathname === to;
            const locked = premium && !isPremium;
            return (
              <Link
                key={to}
                to={to}
                onClick={(e) => handleNav(e, link)}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={`group flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-bold transition-colors ${
                  active ? "bg-nerd-light text-white" : "text-black hover:text-black hover:bg-black/5"
                }`}
              >
                <span>{label}</span>
                {locked && <Lock className="w-3 h-3 opacity-40 group-hover:opacity-70 transition-opacity" />}
              </Link>
            );
          })}
          {user?.is_admin && (
            <Link to="/admin" data-testid="nav-admin"
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-bold transition-colors ${
                pathname === "/admin" ? "bg-nerd-light text-white" : "text-black hover:text-black hover:bg-black/5"}`}>
              <span>Admin</span>
            </Link>
          )}
          {user?.is_creator && (
            <Link to="/creator" data-testid="nav-creator"
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-bold transition-colors ${
                pathname === "/creator" ? "bg-nerd-light text-white" : "text-black hover:text-black hover:bg-black/5"}`}>
              <span>Creator Studio</span>
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {!isPremium && (
                <button data-testid="nav-upgrade-btn" onClick={() => setGate({ label: "Premium", mode: "upgrade" })}
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold bg-nerd-light text-white px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity">
                  <Crown className="w-3.5 h-3.5" /> Go Premium
                </button>
              )}
              <Link to="/profile" data-testid="nav-profile" className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-black/5 transition-colors">
                <Avatar user={user} />
                <span className="hidden sm:inline text-sm font-medium text-nerd-light/90">{user.name}</span>
                {isPremium && <Crown className="w-3.5 h-3.5 text-nerd-light" title="Premium" />}
              </Link>
              <Link to="/settings" data-testid="nav-settings" title="Settings" className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center text-nerd-light/60 hover:text-nerd-light transition-colors">
                <Settings className="w-4 h-4" />
              </Link>
              <button data-testid="nav-logout" onClick={logout} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center text-nerd-light/60 hover:text-coral transition-colors" title="Log out">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-black hover:text-black px-3 py-2 transition-colors">Log in</Link>
              <button data-testid="nav-signup" onClick={() => navigate("/register")} className="text-sm font-semibold bg-nerd-light text-white px-4 py-2 rounded-full hover:opacity-90 transition-opacity">
                Sign up
              </button>
            </>
          )}
          </div>
          {user ? (
            <button
              data-testid="nav-mobile-basket"
              onClick={goSupplies}
              className="md:hidden relative w-10 h-10 rounded-full bg-black/5 border border-black/10 flex items-center justify-center text-nerd-light hover:bg-black/10 transition-colors"
              aria-label="Shopping list"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 22 }} aria-hidden="true">shopping_basket</span>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-green-400 ring-2 ring-nerd-card" />
            </button>
          ) : (
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setMenuOpen((o) => !o)}
              className="md:hidden w-10 h-10 rounded-full bg-black/5 border border-black/10 flex items-center justify-center text-nerd-light hover:bg-black/10 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-black/8 bg-nerd-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col gap-1">
            {links.map((link) => {
              const { to, label, icon: Icon, premium } = link;
              const active = pathname === to;
              const locked = premium && !isPremium;
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={(e) => { handleNav(e, link); setMenuOpen(false); }}
                  data-testid={`nav-mobile-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`group flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium transition-colors ${
                    active ? "bg-nerd-light text-white" : "text-nerd-light/80 hover:bg-black/5"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                  {locked && <Lock className="w-3 h-3 opacity-40 ml-auto" />}
                </Link>
              );
            })}
            {user?.is_admin && (
              <Link to="/admin" data-testid="nav-mobile-admin" onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium transition-colors ${
                  pathname === "/admin" ? "bg-nerd-light text-white" : "text-nerd-light/80 hover:bg-black/5"}`}>
                <ShieldCheck className="w-4 h-4" />
                <span>Admin</span>
              </Link>
            )}
            {user?.is_creator && (
              <Link to="/creator" data-testid="nav-mobile-creator" onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium transition-colors ${
                  pathname === "/creator" ? "bg-nerd-light text-white" : "text-nerd-light/80 hover:bg-black/5"}`}>
                <ChefHat className="w-4 h-4" />
                <span>Creator Studio</span>
              </Link>
            )}
            {user && !isPremium && (
              <button data-testid="nav-mobile-upgrade" onClick={() => { setMenuOpen(false); setGate({ label: "Premium", mode: "upgrade" }); }}
                className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-bold text-nerd-light hover:bg-black/5 transition-colors">
                <Crown className="w-4 h-4" />
                <span>Go Premium</span>
              </button>
            )}
            <div className="h-px bg-black/8 my-1" />
            {user ? (
              <>
                <Link to="/profile" data-testid="nav-mobile-profile" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium text-nerd-light/80 hover:bg-black/5 transition-colors">
                  <Avatar user={user} size="w-5 h-5" />
                  <span>{user.name || "Profile"}</span>
                  {isPremium && <Crown className="w-3.5 h-3.5 text-nerd-light ml-auto" />}
                </Link>
                <Link to="/settings" data-testid="nav-mobile-settings" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium text-nerd-light/80 hover:bg-black/5 transition-colors">
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </Link>
                <button data-testid="nav-mobile-logout" onClick={() => { setMenuOpen(false); logout(); }}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium text-nerd-light/80 hover:bg-black/5 hover:text-coral transition-colors">
                  <LogOut className="w-4 h-4" />
                  <span>Log out</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-mobile-login" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium text-nerd-light/80 hover:bg-black/5 transition-colors">
                  <span>Log in</span>
                </Link>
                <button data-testid="nav-mobile-signup" onClick={() => { setMenuOpen(false); navigate("/register"); }}
                  className="flex items-center justify-center gap-2 px-3.5 py-3 rounded-xl text-sm font-bold bg-nerd-light text-white hover:opacity-90 transition-opacity">
                  <span>Sign up free</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!gate} onOpenChange={(o) => !o && closeGate()}>
        <DialogContent data-testid="premium-gate-dialog" className="bg-white border-black/10 text-nerd-light max-w-sm text-center">
          <div className="flex flex-col items-center pt-2">
            <div className="w-14 h-14 rounded-2xl bg-nerd-light flex items-center justify-center mb-4">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-1.5">Premium feature</h2>
            <p className="text-sm text-nerd-light/60 leading-relaxed mb-1">
              <span className="font-semibold text-nerd-light/80">{gate?.label}</span> is part of Premium — the AI Creative Agent, meal planner and supplies.
            </p>
            <p className="text-sm text-nerd-light/80 font-semibold mb-6">
              {!user?.has_used_trial ? "Free for 7 days, then €8.99 / month · cancel anytime" : "€8.99 / month · cancel anytime"}
            </p>

            {gate?.mode === "upgrade" ? (
              <div className="flex flex-col items-stretch gap-2 w-full">
                <button
                  data-testid="premium-gate-upgrade"
                  onClick={() => { closeGate(); startCheckout(); }}
                  className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold bg-nerd-light text-white px-4 py-2.5 rounded-full hover:opacity-90 transition-opacity"
                >
                  <Crown className="w-4 h-4" /> {!user?.has_used_trial ? "Start 7-day free trial" : "Subscribe — €8.99/mo"}
                </button>
                <button data-testid="premium-gate-later" onClick={closeGate}
                  className="w-full text-sm font-medium text-nerd-light/50 hover:text-nerd-light py-1.5 transition-colors">
                  Maybe later
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 w-full">
                <button
                  data-testid="premium-gate-signup"
                  onClick={() => { closeGate(); navigate("/register"); }}
                  className="flex-1 text-sm font-semibold bg-nerd-light text-white px-4 py-2.5 rounded-full hover:opacity-90 transition-opacity"
                >
                  Sign up free
                </button>
                <button
                  data-testid="premium-gate-login"
                  onClick={() => { closeGate(); navigate("/login"); }}
                  className="flex-1 text-sm font-semibold border border-black/12 px-4 py-2.5 rounded-full hover:bg-black/5 transition-colors"
                >
                  Log in
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
